<template>
  <div
    class="grid gap-4"
    data-sentry-unmask
  >
    <h1 class="text-sm font-semibold">Identifiers ({{ totalIdentifiers }})</h1>
    <div class="space-y-3">
      <identifiers-search-field
        size="sm"
        :has-shortcut="false"
        :store="identifierStore"
      />
      <p
        v-if="searchText && identifiers.length"
        v-html="paginationSummary"
        class="text-xs mx-2"
      />
    </div>
    <template v-if="isFetching && !isFetchingNextPage">
      <div class="skeleton h-116" />
    </template>
    <template v-else-if="!identifiers.length">
      <app-empty-state
        size="sm"
        container-class="py-40 bg-gray-100 rounded-xl"
        icon="face-sad"
        title="No identifiers matched your search"
        :description="`We couldn't find anything matching “${searchText}” in identifiers. Please try again.`"
      />
    </template>
    <template v-else>
      <div class="grid gap-4">
        <agency-identifiers-list-card
          v-if="nonPayableIdentifiers.length"
          :identifiers="nonPayableIdentifiers"
        />
        <agency-identifiers-list-card
          v-if="payableIdentifiers.length"
          :identifiers="payableIdentifiers"
          payable
        />
        <button
          v-if="hasNextPage"
          @click="fetchNextPage"
          class="btn btn-secondary"
        >
          {{ isFetchingNextPage ? 'Loading...' : 'Load more' }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, toRef, watch } from 'vue';
import { useIdentifierStore } from '@/stores/identifiers';
import { useInfiniteIdentifiersByAgencyId } from '@/services/identifiers/queries';
import { useFiltersReset } from '@/composables/useFiltersReset';
import AgencyIdentifiersListCard from '@/components/agencies/AgencyIdentifiersListCard.vue';
import IdentifiersSearchField from '@/components/identifiers/IdentifiersSearchField.vue';

const props = defineProps({
  agencyId: {
    type: String,
    required: true,
  },
});

const identifierStore = useIdentifierStore();
const searchText = computed(() => identifierStore.filters.search_text);

useFiltersReset(identifierStore);

watch(
  () => props.agencyId,
  () => {
    identifierStore.resetFilters();
  }
);

const {
  data: identifiersData,
  isFetching,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
} = useInfiniteIdentifiersByAgencyId(toRef(props, 'agencyId'));

const identifiers = computed(() => identifiersData.value?.pages?.flatMap((page) => page.data) ?? []);
const totalIdentifiers = computed(() => identifiersData.value?.pages.at(0)?.meta?.pagination?.total ?? 0);
const payableIdentifiers = computed(() => identifiers.value.filter((identifier) => identifier.transfer_party));
const nonPayableIdentifiers = computed(() => identifiers.value.filter((identifier) => !identifier.transfer_party));

const paginationSummary = computed(() => {
  return `Showing ${identifiers.value.length} of ${totalIdentifiers.value} results for <span class="text-brand-600">'${searchText.value}'</span>`;
});
</script>
